<template>
  <div class="payment-container">
    <!-- 顶部导航 -->
    <div class="nav-bar">
      <div class="nav-content">
        <div class="logo">
          <span class="glow-text">云商城</span>
        </div>
        <div class="nav-menu">
          <el-menu mode="horizontal" default-active="0" class="nav-menu-inner">
            <el-menu-item index="1" @click="goToHome">首页</el-menu-item>
            <el-menu-item index="3" @click="goToOrders">我的订单</el-menu-item>
          </el-menu>
        </div>
        <div class="user-info">
          <el-dropdown @command="handleCommand">
            <span class="el-dropdown-link">
              {{ userNickname || '用户' }}
              <el-icon class="el-icon--right"><ArrowDown /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="home">返回首页</el-dropdown-item>
                <el-dropdown-item command="orders">我的订单</el-dropdown-item>
                <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="main-content">
      <!-- 订单未找到 -->
      <template v-if="!order">
        <div class="empty-state glassmorphism">
          <el-icon :size="64" color="#ccc"><WarningFilled /></el-icon>
          <h2>订单不存在</h2>
          <p>未找到该订单信息，请返回订单列表</p>
          <button class="btn-primary" @click="goToOrders">返回订单列表</button>
        </div>
      </template>

      <template v-else>
        <div class="payment-layout">
          <!-- 左侧：订单信息 + 支付方式 -->
          <div class="payment-left">
            <!-- 订单摘要 -->
            <div class="order-summary glassmorphism">
              <div class="section-title">
                <el-icon :size="20" color="#537ce5"><Document /></el-icon>
                <span>订单信息</span>
              </div>
              <div class="order-meta">
                <div class="meta-row">
                  <span class="meta-label">订单号</span>
                  <span class="meta-value order-no">{{ order.orderNo }}</span>
                </div>
                <div class="meta-row">
                  <span class="meta-label">下单时间</span>
                  <span class="meta-value">{{ order.createTime }}</span>
                </div>
                <div class="meta-row">
                  <span class="meta-label">商品数量</span>
                  <span class="meta-value">{{ order.totalQuantity }} 件</span>
                </div>
              </div>
              <!-- 商品明细折叠 -->
              <el-collapse v-model="showItems" class="items-collapse">
                <el-collapse-item title="查看商品明细" name="items">
                  <div v-for="item in order.items" :key="item.id" class="summary-item">
                    <div class="si-image">
                      <el-image :src="item.picUrl || defaultImage" fit="cover" class="si-img" />
                    </div>
                    <div class="si-info">
                      <span class="si-title">{{ item.title || item.name }}</span>
                      <span class="si-qty">x{{ item.quantity }}</span>
                    </div>
                    <span class="si-price">¥{{ (item.price * item.quantity).toFixed(2) }}</span>
                  </div>
                </el-collapse-item>
              </el-collapse>
            </div>

            <!-- 支付方式选择 -->
            <div class="payment-methods glassmorphism">
              <div class="section-title">
                <el-icon :size="20" color="#537ce5"><CreditCard /></el-icon>
                <span>选择支付方式</span>
              </div>
              <div class="methods-grid">
                <div
                  v-for="method in paymentMethods"
                  :key="method.id"
                  class="method-card"
                  :class="{ active: selectedMethod === method.id }"
                  @click="selectMethod(method.id)"
                >
                  <div class="method-icon" :style="{ background: method.bgColor }">
                    <span class="method-icon-text" :style="{ color: method.iconColor }">{{ method.iconText }}</span>
                  </div>
                  <div class="method-info">
                    <h4>{{ method.name }}</h4>
                    <p>{{ method.desc }}</p>
                  </div>
                  <div class="method-check" v-if="selectedMethod === method.id">
                    <el-icon :size="22" color="#537ce5"><CircleCheckFilled /></el-icon>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 右侧：支付确认卡片 -->
          <div class="payment-right">
            <div class="pay-card glassmorphism">
              <div class="pay-card-header">
                <el-icon :size="24" color="#ff4d4f"><Wallet /></el-icon>
                <span>支付金额</span>
              </div>
              <div class="pay-amount">
                <span class="currency">¥</span>
                <span class="amount">{{ order.totalAmount }}</span>
              </div>
              <div class="pay-method-label" v-if="selectedMethodObj">
                <span>支付方式：{{ selectedMethodObj.name }}</span>
              </div>

              <!-- 倒计时 -->
              <div class="countdown" v-if="countdown > 0">
                <el-icon :size="14"><Clock /></el-icon>
                <span>请在 <strong>{{ countdownText }}</strong> 内完成支付</span>
              </div>
              <div class="countdown expired" v-else>
                <span>订单已超时，请重新下单</span>
              </div>

              <button
                class="btn-pay"
                :class="{ paying: paying }"
                :disabled="!selectedMethod || paying || countdown <= 0"
                @click="handlePay"
              >
                <el-icon v-if="!paying"><CreditCard /></el-icon>
                <el-icon v-else class="spin"><Loading /></el-icon>
                {{ paying ? '支付处理中...' : '确认支付' }}
              </button>

              <button class="btn-cancel" @click="cancelPay">
                取消支付
              </button>

              <!-- 支付安全提示 -->
              <div class="security-tip">
                <el-icon :size="14" color="#999"><Lock /></el-icon>
                <span>支付环境安全，您的信息已加密保护</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- 支付成功弹窗 -->
    <el-dialog
      v-model="showSuccess"
      :show-close="false"
      :close-on-click-modal="false"
      width="420px"
      center
      class="success-dialog"
    >
      <div class="success-content">
        <div class="success-icon-wrapper">
          <el-icon :size="64" color="#43b581"><CircleCheckFilled /></el-icon>
        </div>
        <h2>支付成功！</h2>
        <p class="success-amount">您已成功支付 <strong>¥{{ order?.totalAmount }}</strong></p>
        <p class="success-method">支付方式：{{ selectedMethodObj?.name }}</p>
        <p class="success-order">订单号：{{ order?.orderNo }}</p>
        <div class="success-actions">
          <button class="btn-primary" @click="goToOrders">查看订单</button>
          <button class="btn-outline" @click="goToHome">继续购物</button>
        </div>
      </div>
    </el-dialog>

    <!-- 底部 -->
    <div class="footer">
      <div class="footer-content">
        <div class="footer-section">
          <h4>关于我们</h4>
          <p>云商城是一个基于微服务架构的现代化交易平台。</p>
        </div>
        <div class="footer-section">
          <h4>快速链接</h4>
          <ul>
            <li><a href="#">帮助中心</a></li>
            <li><a href="#">联系我们</a></li>
            <li><a href="#">服务条款</a></li>
          </ul>
        </div>
        <div class="footer-section">
          <h4>联系方式</h4>
          <p>Email: support@cloudshop.com</p>
          <p>Phone: 400-800-8888</p>
        </div>
      </div>
      <div class="footer-bottom">
        <p>&copy; 2026 云商城 - 微服务交易平台. All rights reserved.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage, ElNotification } from 'element-plus'
import {
  ArrowDown, CreditCard, Document, CircleCheckFilled,
  Wallet, Clock, Lock, Loading, WarningFilled
} from '@element-plus/icons-vue'
import { orderService, getCurrentUserId } from '../api/order'

const router = useRouter()
const route = useRoute()
const userNickname = ref('')
const order = ref(null)
const selectedMethod = ref('')
const paying = ref(false)
const showSuccess = ref(false)
const showItems = ref([])
const countdown = ref(30 * 60) // 30分钟倒计时
let timer = null
const defaultImage = 'https://via.placeholder.com/300x300?text=No+Image'

/** 支付方式列表 */
const paymentMethods = ref([
  {
    id: 'alipay',
    name: '支付宝',
    desc: '推荐支付宝用户使用，支持余额、花呗、信用卡',
    iconText: '支',
    iconColor: '#1677ff',
    bgColor: 'rgba(22, 119, 255, 0.1)',
  },
  {
    id: 'wechat',
    name: '微信支付',
    desc: '支持微信零钱、银行卡快捷支付',
    iconText: '微',
    iconColor: '#07c160',
    bgColor: 'rgba(7, 193, 96, 0.1)',
  },
  {
    id: 'unionpay',
    name: '银联云闪付',
    desc: '支持银联卡、云闪付APP扫码支付',
    iconText: '银',
    iconColor: '#e60012',
    bgColor: 'rgba(230, 0, 18, 0.1)',
  },
  {
    id: 'creditcard',
    name: '信用卡支付',
    desc: '支持Visa、MasterCard、JCB等国际信用卡',
    iconText: '卡',
    iconColor: '#8b5fc9',
    bgColor: 'rgba(139, 95, 201, 0.1)',
  },
  {
    id: 'huabei',
    name: '花呗分期',
    desc: '支持3/6/12期分期，手续费低至0.5%',
    iconText: '花',
    iconColor: '#ff6b35',
    bgColor: 'rgba(255, 107, 53, 0.1)',
  },
  {
    id: 'balance',
    name: '余额支付',
    desc: '使用云商城账户余额直接抵扣',
    iconText: '余',
    iconColor: '#537ce5',
    bgColor: 'rgba(83, 124, 229, 0.1)',
  },
])

/** 选中的支付方式对象 */
const selectedMethodObj = computed(() => {
  return paymentMethods.value.find(m => m.id === selectedMethod.value) || null
})

/** 倒计时文本 */
const countdownText = computed(() => {
  const m = Math.floor(countdown.value / 60)
  const s = countdown.value % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
})

/** 选择支付方式 */
const selectMethod = (id) => {
  selectedMethod.value = id
}

/** 确认支付 */
const handlePay = async () => {
  if (!selectedMethod.value) {
    ElMessage.warning('请选择支付方式')
    return
  }
  if (countdown.value <= 0) {
    ElMessage.error('订单已超时，请返回重新下单')
    return
  }

  paying.value = true

  try {
    // 1秒等待动画
    await new Promise(resolve => setTimeout(resolve, 1000))

    // 调用后端支付 API
    await orderService.payOrder(order.value.id)

    // 更新订单状态
    order.value.status = 1 // 已支付
    order.value.payMethod = selectedMethodObj.value?.name
    order.value.payTime = formatTime(new Date())

    paying.value = false
    showSuccess.value = true

    // 停止倒计时
    if (timer) {
      clearInterval(timer)
      timer = null
    }
  } catch (error) {
    console.error('支付失败:', error)
    paying.value = false
    ElMessage.error('支付失败，请稍后重试')
  }
}

/** 取消支付 */
const cancelPay = () => {
  router.push('/orders')
}

/** 格式化时间 */
const formatTime = (date) => {
  const d = date || new Date()
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

/** 加载订单 */
const loadOrder = async () => {
  const orderId = route.query.orderId
  if (!orderId) { 
    order.value = null
    return 
  }

  const userId = getCurrentUserId()
  if (!userId) {
    ElMessage.warning('请先登录')
    router.push('/login')
    return
  }

  try {
    // 从后端加载订单列表
    const res = await orderService.getOrderList(userId)
    const data = res.data?.data || res.data?.records || res.data
    
    if (Array.isArray(data)) {
      const found = data.find(o => String(o.id) === String(orderId))
      if (found) {
        order.value = {
          id: found.id,
          orderNo: found.orderNo || `OD${found.id}`,
          createTime: found.createTime || found.orderTime || formatTime(new Date()),
          status: found.status ?? 0,
          totalAmount: found.totalAmount || found.totalPrice || 0,
          totalQuantity: found.totalQuantity || found.count || 1,
          items: found.items || found.orderItems || [{
            id: found.productId,
            productId: found.productId,
            title: found.productName || '商品',
            name: found.productName,
            price: found.totalAmount || 0,
            quantity: found.count || 1,
            picUrl: found.picUrl || '',
          }]
        }
      } else {
        order.value = null
      }
    } else {
      order.value = null
    }
  } catch (error) {
    console.error('加载订单失败:', error)
    order.value = null
  }
}

/** 启动倒计时 */
const startCountdown = () => {
  timer = setInterval(() => {
    if (countdown.value > 0) {
      countdown.value--
    } else {
      clearInterval(timer)
      timer = null
    }
  }, 1000)
}

/** 导航 */
const goToHome = () => router.push('/home')
const goToOrders = () => router.push('/orders')

const handleCommand = (command) => {
  if (command === 'logout') {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    ElNotification({ title: '再见', message: '已成功退出登录', type: 'info', duration: 1800 })
    router.push('/login')
  } else if (command === 'home') {
    goToHome()
  } else if (command === 'orders') {
    goToOrders()
  }
}

onMounted(async () => {
  const userStr = localStorage.getItem('user')
  if (userStr) {
    try {
      const user = JSON.parse(userStr)
      userNickname.value = user.nickname || user.username || '用户'
    } catch (e) { console.error(e) }
  }
  await loadOrder()
  if (order.value) {
    startCountdown()
  }
})

onUnmounted(() => {
  if (timer) { clearInterval(timer); timer = null }
})
</script>

<style scoped>
.payment-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #232a4d 0%, #1a1f3a 50%, #2d1b3d 100%);
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
}

/* ========== 导航栏 ========== */
.nav-bar {
  background: linear-gradient(135deg, #232a4d 0%, #4b2067 100%);
  box-shadow: 0 2px 8px #0000150d;
  position: sticky; top: 0; z-index: 1000;
  backdrop-filter: blur(10px);
}
.nav-content { max-width: 1400px; margin: 0 auto; padding: 12px 20px; display: flex; align-items: center; justify-content: space-between; }
.logo { font-size: 24px; font-weight: bold; margin-right: 40px; min-width: 120px; }
.glow-text { color: #fff; text-shadow: 0 0 8px #a3a3ff, 0 0 16px #4b2067; }
.nav-menu { flex: 1; }
.nav-menu-inner { background: transparent !important; border-bottom: none !important; }
.nav-menu-inner :deep(.el-menu-item) { color: #fff !important; background: transparent !important; font-size: 0.95rem; }
.nav-menu-inner :deep(.el-menu-item:hover) { background: rgba(255,255,255,0.1) !important; }
.user-info { margin-left: auto; color: #fff; font-size: 14px; }
.user-info :deep(.el-dropdown-link) { color: #fff; cursor: pointer; display: flex; align-items: center; gap: 8px; }
.user-info :deep(.el-dropdown-link:hover) { color: #a3a3ff; }

/* ========== 主内容区 ========== */
.main-content {
  flex: 1; max-width: 1100px; margin: 0 auto; width: 100%;
  padding: 28px 16px 40px; box-sizing: border-box;
}

.glassmorphism {
  background: rgba(255,255,255,0.08);
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.3);
  color: #fff;
}

/* 布局 */
.payment-layout {
  display: flex; gap: 24px; align-items: flex-start;
}
.payment-left { flex: 1; display: flex; flex-direction: column; gap: 20px; }
.payment-right { width: 340px; min-width: 340px; position: sticky; top: 80px; }

/* ========== 订单摘要 ========== */
.order-summary { padding: 24px; }
.section-title { display: flex; align-items: center; gap: 8px; font-size: 1.05rem; font-weight: 700; color: rgba(255,255,255,0.95); margin-bottom: 16px; }

.order-meta { display: flex; flex-direction: column; gap: 10px; margin-bottom: 16px; }
.meta-row { display: flex; justify-content: space-between; align-items: center; }
.meta-label { font-size: 0.9rem; color: rgba(255,255,255,0.6); }
.meta-value { font-size: 0.9rem; color: rgba(255,255,255,0.9); font-weight: 500; }
.order-no { font-family: 'Courier New', monospace; color: #a3a3ff; font-weight: 600; }

.items-collapse :deep(.el-collapse-item__header) {
  font-size: 0.88rem; color: #a3a3ff; font-weight: 500; background: transparent;
}

.summary-item {
  display: flex; align-items: center; gap: 10px; padding: 8px 0;
  border-bottom: 1px solid rgba(255,255,255,0.05);
}
.summary-item:last-child { border-bottom: none; }
.si-image { flex-shrink: 0; width: 44px; height: 44px; border-radius: 8px; overflow: hidden; }
.si-img { width: 44px; height: 44px; }
.si-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.si-title { font-size: 0.85rem; color: rgba(255,255,255,0.9); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.si-qty { font-size: 0.75rem; color: rgba(255,255,255,0.5); }
.si-price { flex-shrink: 0; font-size: 0.88rem; color: #ff7f7f; font-weight: 600; }

/* ========== 支付方式 ========== */
.payment-methods { padding: 24px; }
.methods-grid { display: flex; flex-direction: column; gap: 10px; }

.method-card {
  display: flex; align-items: center; gap: 14px; padding: 14px 18px;
  border: 2px solid rgba(255,255,255,0.1); border-radius: 14px;
  background: rgba(255,255,255,0.03);
  cursor: pointer; transition: all 0.3s ease; position: relative;
}
.method-card:hover { border-color: rgba(163,163,255,0.35); background: rgba(163,163,255,0.08); }
.method-card.active {
  border-color: #a3a3ff; background: rgba(163,163,255,0.12);
  box-shadow: 0 4px 16px rgba(163,163,255,0.25);
}

.method-icon {
  flex-shrink: 0; width: 48px; height: 48px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
}
.method-icon-text { font-size: 1.3rem; font-weight: 800; }

.method-info { flex: 1; }
.method-info h4 { margin: 0 0 2px; font-size: 0.95rem; color: rgba(255,255,255,0.95); font-weight: 600; }
.method-info p { margin: 0; font-size: 0.78rem; color: rgba(255,255,255,0.5); line-height: 1.4; }

.method-check { flex-shrink: 0; }

/* ========== 支付卡片 ========== */
.pay-card {
  padding: 28px 24px; display: flex; flex-direction: column; align-items: center; gap: 16px;
}

.pay-card-header { display: flex; align-items: center; gap: 8px; font-size: 1rem; font-weight: 600; color: rgba(255,255,255,0.95); }

.pay-amount { text-align: center; }
.currency { font-size: 1.4rem; color: #ff7f7f; font-weight: 700; vertical-align: top; line-height: 2.4; }
.amount { font-size: 2.8rem; color: #ff7f7f; font-weight: 800; letter-spacing: -1px; }

.pay-method-label { font-size: 0.85rem; color: rgba(255,255,255,0.6); }

.countdown {
  display: flex; align-items: center; gap: 6px; font-size: 0.82rem; color: #ffb347;
  padding: 6px 14px; background: rgba(255,179,71,0.15); border-radius: 8px;
}
.countdown strong { color: #ffa500; }
.countdown.expired { color: #ff7f7f; background: rgba(255,127,127,0.15); }

.btn-pay {
  width: 100%; padding: 14px; border: none; border-radius: 12px;
  background: linear-gradient(135deg, #4e9aff 0%, #3b82f6 100%);
  color: #fff; font-size: 1.05rem; font-weight: 700; cursor: pointer;
  transition: all 0.3s ease; display: flex; align-items: center; justify-content: center; gap: 8px;
  letter-spacing: 1px;
}
.btn-pay:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(59,130,246,0.5); }
.btn-pay:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
.btn-pay.paying { background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%); }

.btn-cancel {
  width: 100%; padding: 10px; border: 1px solid rgba(255,255,255,0.15); border-radius: 10px;
  background: transparent; color: rgba(255,255,255,0.6); font-size: 0.9rem; cursor: pointer;
  transition: all 0.25s ease;
}
.btn-cancel:hover { border-color: #ff7f7f; color: #ff7f7f; }

.security-tip {
  display: flex; align-items: center; gap: 6px; font-size: 0.75rem; color: rgba(255,255,255,0.4);
}

/* ========== 成功弹窗 ========== */
.success-dialog :deep(.el-dialog) { border-radius: 20px !important; }
.success-dialog :deep(.el-dialog__body) { padding: 20px 28px 28px; }

.success-content { display: flex; flex-direction: column; align-items: center; text-align: center; gap: 8px; }
.success-icon-wrapper {
  width: 96px; height: 96px; border-radius: 50%;
  background: rgba(67,181,129,0.1); display: flex; align-items: center; justify-content: center;
  margin-bottom: 8px;
}
.success-content h2 { font-size: 1.4rem; color: #232a4d; margin: 0; }
.success-amount { font-size: 1rem; color: #333; margin: 0; }
.success-amount strong { color: #ff4d4f; font-size: 1.15rem; }
.success-method, .success-order { font-size: 0.85rem; color: #999; margin: 0; }
.success-actions { display: flex; gap: 12px; margin-top: 16px; width: 100%; }

.btn-primary {
  flex: 1; padding: 10px 24px; border: none; border-radius: 10px;
  background: linear-gradient(135deg, #537ce5 0%, #8b5fc9 100%);
  color: #fff; font-size: 0.95rem; font-weight: 600; cursor: pointer;
  transition: all 0.3s ease;
}
.btn-primary:hover { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(83,124,229,0.4); }

.btn-outline {
  flex: 1; padding: 10px 24px; border: 1px solid rgba(0,0,0,0.1); border-radius: 10px;
  background: transparent; color: #666; font-size: 0.95rem; font-weight: 500;
  cursor: pointer; transition: all 0.25s ease;
}
.btn-outline:hover { border-color: #537ce5; color: #537ce5; }

/* ========== 空状态 ========== */
.empty-state {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 80px 20px; text-align: center; gap: 12px;
}
.empty-state h2 { font-size: 1.25rem; color: rgba(255,255,255,0.9); margin: 0; }
.empty-state p { font-size: 0.95rem; color: rgba(255,255,255,0.5); margin: 0; }

/* ========== Loading ========== */
.spin { animation: spin 1s linear infinite; }
@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

/* ========== 底部 ========== */
.footer { background: #232a4d; color: #fff; margin-top: auto; padding: 30px 20px 15px; }
.footer-content { max-width: 1400px; margin: 0 auto; display: grid; grid-template-columns: repeat(auto-fit, minmax(250px,1fr)); gap: 30px; margin-bottom: 25px; }
.footer-section h4 { margin-bottom: 16px; font-size: 1.1rem; }
.footer-section p { font-size: 0.9rem; opacity: 0.8; margin-bottom: 8px; }
.footer-section ul { list-style: none; padding: 0; }
.footer-section li { margin-bottom: 8px; }
.footer-section a { color: #a3a3ff; text-decoration: none; transition: color 0.3s; }
.footer-section a:hover { color: #fff; }
.footer-bottom { border-top: 1px solid #4b206730; padding-top: 20px; text-align: center; font-size: 0.85rem; opacity: 0.7; }

/* ========== 响应式 ========== */
@media (max-width: 768px) {
  .payment-layout { flex-direction: column; }
  .payment-right { width: 100%; min-width: auto; position: static; }
  .pay-card { padding: 20px 16px; }
  .method-card { padding: 12px 14px; }
  .method-icon { width: 40px; height: 40px; }
  .method-icon-text { font-size: 1.1rem; }
  .nav-content { flex-wrap: wrap; gap: 12px; }
  .nav-menu { width: 100%; }
  .user-info { width: 100%; margin-left: 0; }
}
</style>
